import React from "react";
import { Link } from "react-router-dom";

import foodImg from "../../assets/images/Home/food-delivery.webp";
import flowerImg from "../../assets/images/Home/flowers-delivery.webp";
import eventImg from "../../assets/images/Home/special-events.png";
import airImg from "../../assets/images/Home/air-freight.webp";
import dairyImg from "../../assets/images/Home/dairy-products.png";
import meatImg from "../../assets/images/Home/refrigerated-meat-transport-chiller-van-uae-chillfreeze.webp";
import poultryImg from "../../assets/images/Home/poultry-products.webp";
import frozenImg from "../../assets/images/Home/goods-transport.png";
import dryImg from "../../assets/images/Home/high-roof-van-service-chiller-dubai-sharjah-rent-freeze.webp";
import assignmentImg from "../../assets/images/Home/chiller-van-for-refrigerated-transport.webp";

export const servicesSections = [
  {
    type: "hero",
    title: "Refrigerated Transport Services in Dubai & Sharjah for Cold Chain Operations Across the UAE",
    paragraphs: [
      "Operating cold chain logistics in the UAE means managing cargo movement under some of the most demanding climate conditions in the world. Businesses in food distribution, dairy supply, pharmaceutical, and retail sectors require refrigerated transport that maintains consistent temperature conditions from loading through to final delivery across every route.",
      "Chiller Fleet Transport manages refrigerated transport services in Dubai, Sharjah, and the wider UAE through a structured fleet of temperature-controlled vans and trucks, supporting businesses operating daily distribution cycles, bulk refrigerated supply movement, and time-sensitive cold chain logistics across the UAE.",
      "Contact our team to arrange refrigerated transport across Dubai, Sharjah, and UAE.",
    ],
  },

  {
    type: "service-categories",
    title: "Refrigerated & Cold Chain Transport Services We Operate Across the UAE",
    categories: [
      {
        icon: "bi-bag-check",
        title: "Food Delivery Transport",
        image: foodImg,
        description:
          <>Urban food distribution across Dubai and Sharjah involves multiple delivery stops, tight timing windows, and routes that pass through high-traffic commercial zones. Central kitchens, restaurants, and supermarkets require chilled movement where internal cargo temperature stays consistent across the full delivery cycle regardless of stop frequency or route duration. This transport category is handled through <Link to="/chiller-van-for-rent" className="internal-link">chiller vans</Link> and <Link to="/freezer-van-for-rent" className="internal-link">freezer vans</Link> deployed on structured urban routes.</>,
      },
      {
        icon: "bi-flower1",
        title: "Flower & Perishable Transport",
        image: flowerImg,
        description:
          "UAE summer heat affects flowers faster than almost any other cargo. Beyond temperature, humidity fluctuation during loading and unloading causes rapid deterioration that standard packaging cannot prevent. For event distribution, wedding logistics, and retail flower supply across Dubai and Sharjah, transport timing and vehicle preparation before loading are more critical than route distance. Small chiller vans are used for this cargo category.",
      },
      {
        icon: "bi-calendar-event",
        title: "Special Event & Catering Logistics",
        image: eventImg,
        description:
          "Unlike routine distribution, event logistics operate on fixed timelines with no room for delay or rescheduling. Catering movement for hotels, corporate events, and large gatherings across Dubai requires coordinated dispatch where multiple vehicles are sometimes deployed simultaneously to a single destination. Temperature maintenance during staging and unloading is as important as transit itself. Freezer vans and refrigerated trucks are allocated based on catering volume and event scale.",
      },
      {
        icon: "bi-airplane",
        title: "Air Freight Ground Transport",
        image: airImg,
        description:
          "DXB airport cargo movement operates within strict handover windows where vehicle readiness directly affects clearance timing. Temperature-sensitive shipments transferring between airport facilities, cold storage warehouses, and onward distribution points cannot tolerate delays caused by unprepared vehicles or unplanned routing. Every vehicle assigned to airport cargo runs is pre-cooled and route-confirmed before departure. Refrigerated trucks and chiller vans cover this transport category depending on shipment volume. For rail-linked intermodal cargo requiring ground continuation, the same pre-cleared vehicle deployment applies.",
      },
      {
        icon: "bi-droplet-half",
        title: "Dairy Products",
        image: dairyImg,
        description:
          <>Dairy supply chains across Dubai and Sharjah run on daily cycles where consistency matters as much as temperature range. A chilled environment that fluctuates even slightly across repeated distribution runs affects product shelf life and retail compliance. Unlike one-off deliveries, dairy transport requires vehicles that perform reliably across high-frequency routes serving retail outlets, hotels, and food service operators day after day. Chiller vans and <Link to="/chiller-truck-for-rent" className="internal-link">chiller trucks</Link> handle this category across regular scheduled routes.</>,
      },
      {
        icon: "bi-thermometer-snow",
        title: "Meat Transport",
        image: meatImg,
        description:
          <>From processing facilities to cold storage and onward to retail, meat moves through a regulated chain where any temperature deviation creates both product loss and compliance risk. Sub-zero conditions must be maintained continuously — not just during transit but through loading, staging, and handover. In UAE logistics this means vehicles must be pre-cooled, loaded efficiently, and monitored throughout the route. <Link to="/freezer-truck-for-rent" className="internal-link">Freezer trucks</Link> and <Link to="/refrigerated-trucks-for-rent" className="internal-link">refrigerated trucks</Link> are assigned to meat transport operations.</>,
      },
      {
        icon: "bi-wind",
        title: "Poultry Transport",
        image: poultryImg,
        description:
          "High-volume poultry movement creates a specific logistics pressure — rapid unloading cycles that repeatedly expose cargo to ambient UAE temperatures. Each door opening during delivery adds cumulative heat exposure that builds across a multi-stop route. Managing that exposure through route sequencing and minimizing unloading time at each stop is central to how poultry transport is handled across Dubai and Sharjah. Freezer vans and freezer trucks are used depending on delivery volume.",
      },
      {
        icon: "bi-snow3",
        title: "Frozen Goods & Packaged Food Transport",
        image: frozenImg,
        description:
          "Ice cream, frozen seafood, packaged meals, and similar goods move across warehouse-to-retail routes where sub-zero consistency across the full journey is non-negotiable. The challenge in UAE distribution is maintaining those conditions across longer inter-emirate routes where transit time, ambient heat, and multiple warehouse handovers all create pressure on vehicle cooling systems. Bulk frozen goods transport across Dubai, Sharjah, and wider UAE routes is handled through freezer trucks.",
      },
      {
        icon: "bi-box-seam",
        title: "General Goods & Dry Cargo Transport",
        image: dryImg,
        description:
          <>Not all commercial cargo requires refrigeration. Packaged goods, cartons, manufactured products, and warehouse transfers where volume and space utilization matter more than temperature control are handled through a separate vehicle category. <Link to="/high-roof-dry-van-for-rent" className="internal-link">High roof dry vans</Link> cover general cargo movement across Dubai and Sharjah where load capacity and interior clearance determine vehicle selection rather than thermal requirements.</>,
      },
    ],
  },

  {
    type: "about",
    title: "How We Assign Vehicles Based on Cargo and Route Requirements",
    paragraphs: [
      "Vehicle assignment at Chiller Fleet Transport starts with two questions — what is the cargo, and where is it going. Everything else follows from those answers.",
      "Temperature range comes first. Chilled cargo moving between 0°C and 8°C and frozen cargo requiring below -18°C are handled through entirely separate vehicle categories. Mixing those requirements in a single vehicle without a multi-zone system creates compliance risk, so assignment begins at the thermal requirement level before route planning starts.",
      "Route structure determines vehicle scale. The decision factors we work through for every booking:",
    ],
    image: assignmentImg,
    imageAlt: "Chiller Fleet vehicle assignment process Dubai",
    reverse: true,
    items: [
      {
        title: "Cargo Temperature Requirement",
        description: "Chilled or frozen determines the vehicle category before anything else.",
      },
      {
        title: "Delivery Scale",
        description: "Single warehouse run versus multi-stop urban distribution across Dubai or Sharjah requires different vehicle sizing.",
      },
      {
        title: "Route Type",
        description: "Inter-emirate UAE routes versus short urban runs affect which branch handles dispatch.",
      },
      {
        title: "Timing",
        description: "Scheduled runs are pre-planned from our Dubai or Sharjah branches while same-day urgent requirements are covered through standby vehicles maintained at each branch.",
      },
      {
        title: "Load Volume",
        description: "Smaller chilled or frozen loads moving across Dubai are handled differently from consolidated bulk shipments crossing into Sharjah or beyond.",
      },
    ],

    note:
      "No vehicle leaves our facility without these factors confirmed against the actual booking requirement.",
  },

  {
    type: "cold-chain-standards",
    title: "Chiller Fleet Transport's Cold Chain Handling Standards",
    description:
      "UAE summer temperatures regularly exceed 45°C, and that ambient heat does not just affect cargo — it directly impacts vehicle compressor recovery time. When a refrigerated vehicle is opened at a hot loading dock, internal temperature rises faster than most operators account for, and recovery to the required range takes longer than the same process would in a cooler climate. This is the operational reality that shapes how we handle every transport movement across Dubai and Sharjah.",
    items: [
      {
        icon: "bi-thermometer-sun",
        title: "Pre-Cooling",
        description:
          "Every vehicle is brought to its required temperature range before loading begins. This is not optional — loading does not start until internal temperature is confirmed at the correct level. A vehicle that begins loading at the wrong temperature will not recover during transit under UAE conditions.",
      },
      {
        icon: "bi-door-closed",
        title: "Loading Discipline",
        description:
          "Door exposure during loading is managed through sequenced loading schedules. Cargo is staged and organized before the vehicle door opens, minimizing the time between first opening and final closure at each loading point across Dubai and Sharjah facilities.",
      },
      {
        icon: "bi-speedometer2",
        title: "Transit Monitoring",
        description:
          "During active routes, onboard temperature logging runs continuously. Both the driver and our dispatch team maintain live visibility over cargo temperature throughout the journey — not just at departure and arrival.",
      },
      {
        icon: "bi-signpost-split",
        title: "Route Sequencing",
        description:
          "On multi-stop urban routes across Dubai, delivery order is planned around cumulative door-opening exposure. Stops with the highest unloading volume are sequenced to reduce the total temperature impact across the full route.",
      },
      {
        icon: "bi-truck",
        title: "Standby Deployment",
        description:
          "Each branch across Dubai, Sharjah, and Abu Dhabi maintains a dedicated standby fleet. If a vehicle develops a mechanical or refrigeration issue during an active operation, a replacement is dispatched from the nearest branch without waiting for the original vehicle to return.",
      },
    ],
  },

  {
    type: "zones",
    title: "UAE Logistics Corridors & Distribution Zones We Operate In",
    zones: [
      {
        icon: "bi-buildings",
        name: "Ras Al Khor",
        description:
          "Overlapping warehouse dispatch windows and loading dock congestion mean refrigerated vehicles here must be staged and sequenced before the loading queue begins, not after.",
      },
      {
        icon: "bi-grid-3x3-gap",
        name: "Al Quoz",
        description:
          "Narrow industrial access roads and repeated stop frequency across dense commercial zones make rapid unloading coordination and route flexibility the primary operational pressure.",
      },
      {
        icon: "bi-anchor",
        name: "Jebel Ali",
        description:
          "Customs clearance timing creates waiting-time risk for frozen cargo. Transfer coordination between port release and warehouse receiving schedules determines whether cold chain continuity holds.",
      },
      {
        icon: "bi-airplane-engines",
        name: "DXB Airport Cargo Zone",
        description:
          "Clearance windows are fixed. Vehicles are pre-staged before cargo is released so handover to onward distribution happens without any gap in temperature control.",
      },
      {
        icon: "bi-truck",
        name: "Sharjah Industrial Areas",
        description:
          "Cross-emirate traffic and repeated daily supply loops between Dubai and Sharjah industrial zones require dispatch timing that accounts for border corridor delays.",
      },
      {
        icon: "bi-geo-alt",
        name: "Abu Dhabi Routes",
        description:
          "Extended compressor runtime and long-haul temperature stability across inter-emirate routes create different cold chain pressures than urban Dubai distribution entirely.",
      },
    ],
  },

  {
    type: "about",
    title: "East Coast Distribution Routes",
    paragraphs: [
      "East Coast bookings are dispatched from our Ras Al Khor headquarters or Sharjah branch rather than a local base, which makes departure timing the deciding factor rather than distance. Every Fujairah route is confirmed against cargo release schedules before a vehicle is assigned. Chiller trucks, freezer trucks, and multi-temperature refrigerated trucks are allocated to these routes by cargo temperature and load volume.",
    ],
    items: [
      {
        title: "Fujairah Port",
        description:
          "Seafood landings and containerised food imports clear on port timing, not delivery timing. Vehicles are dispatched to arrive staged before release rather than after, so ground continuation to cold storage begins without the cargo waiting on a truck.",
      },
      {
        title: "Fujairah Free Zone",
        description:
          "Manufacturers, food processors, and commercial importers here run on scheduled collection windows. Free zone bookings are placed against confirmed lead time from our Sharjah branch, with cargo documentation timelines factored into dispatch rather than handled at the gate.",
      },
      {
        title: "Dibba & Northern East Coast",
        description:
          "Deliveries running north from Fujairah City extend transit well beyond the main corridor. These routes are sequenced as single-direction runs with the highest-volume drop placed first, since backtracking across coastal roads adds hours rather than minutes.",
      },
      {
        title: "Fujairah City & Masafi Corridor",
        description:
          "Return-leg consolidation between Fujairah and Dubai is arranged under the same booking where cargo volume supports it. Businesses running regular East Coast supply cycles are scheduled as recurring dispatch slots from whichever branch sits closest to the loading point.",
      },
    ],
  },

  {
    type: "industries",
    title: "Industries & Business Types We Support Across the UAE",
    industries: [
      {
        icon: "bi-arrow-repeat",
        title: "Daily Distribution Businesses",
        description:
          "Restaurants, cafes, central kitchens, and retail chains running daily cold chain cycles across Dubai and Sharjah need vehicles that show up on schedule, every day. For these operations, refrigerated transport is not occasional — it is a fixed operational dependency that runs alongside their core business rhythm.",
      },
      {
        icon: "bi-graph-up-arrow",
        title: "High-Volume Supply Chain Operators",
        description:
          "Bulk refrigerated movement for supermarkets, FMCG distributors, and warehouse operators involves consolidated shipments across multiple UAE locations under structured supply timelines. A missed or delayed transport window at this scale affects downstream stock availability across multiple outlets simultaneously.",
      },
      {
        icon: "bi-clock-history",
        title: "Time-Sensitive & Urgent Logistics",
        description:
          "Pharmaceutical suppliers, import/export operators, and airport cargo handlers cannot work around transport delays. Vehicle readiness is confirmed before the booking is accepted — because for these businesses, an unready vehicle is the same as no vehicle.",
      },
      {
        icon: "bi-calendar-check",
        title: "Seasonal & Event-Based Operations",
        description:
          "Hotels, catering companies, and event organizers don't need permanent fleet contracts — they need capacity that scales during Ramadan, Eid, and major UAE events without the lead time that permanent logistics arrangements require.",
      },
    ],
  },

  {
    type: "cta",
    title: "Ready To Arrange Refrigerated Transport Across Dubai, Sharjah & UAE?",
    paragraphs: [
      <>Our fleet covers both scheduled and same-day refrigerated transport across Dubai, Sharjah, and the wider UAE. Call or WhatsApp <Link to="/contact-us" className="internal-link">our team</Link> to confirm cargo type, route requirements, and refrigerated vehicle availability across the Emirates.</>,
    ],
  },
];
